/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/CardTabLayoutMenu.tsx
 * FILE_ID: UI-KANBAN-CARD-TAB-LAYOUT-MENU-001
 * PURPOSE: Card-tab layout setting — move Action / Team chat / Card notes /
 *   Email / Description up or down, or hide them. Does not delete data.
 * WHY: Operators asked to choose which block they see first.
 * NOTES FOR FUTURE AI: Hidden blocks stay in `layout.order` so Show puts
 *   them back where they were. Email tab is a separate surface and is
 *   never removed by hiding the Email block here.
 */
import { Fragment, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Eye, EyeOff, RotateCcw, SlidersHorizontal } from 'lucide-react'

import {
  CARD_TAB_BLOCK_LABELS,
  cardTabBlockCategory,
  type CardTabBlockId,
  type CardTabLayout,
} from './cardTabLayout'
import type { UseCardTabLayoutResult } from './useCardTabLayout'

export interface CardTabLayoutMenuProps {
  layout: CardTabLayout
  move: UseCardTabLayoutResult['move']
  setHidden: UseCardTabLayoutResult['setHidden']
  reset: UseCardTabLayoutResult['reset']
  /** menu = Card-tab dropdown. inline = settings-home list. */
  variant?: 'menu' | 'inline'
}

export function CardTabLayoutMenu({
  layout,
  move,
  setHidden,
  reset,
  variant = 'menu',
}: CardTabLayoutMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const hiddenSet = new Set(layout.hidden)

  useEffect(() => {
    if (variant !== 'menu' || !open) return
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, variant])

  const editor = (
    <>
      <p className="px-1.5 pb-2 text-[11px] leading-snug text-neutral-500 dark:text-neutral-400">
        Move blocks up or down, or hide them. Hidden blocks stay on the
        card — they just leave this tab.
      </p>
      <ul className="space-y-1">
        {layout.order.map((id, index) => {
          const category = cardTabBlockCategory(id)
          const previous = index > 0 ? cardTabBlockCategory(layout.order[index - 1]) : null
          return (
            <Fragment key={id}>
              {category !== previous ? (
                <li className="list-none px-1.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-400 first:pt-0">
                  {category}
                </li>
              ) : null}
              <LayoutRow
                id={id}
                hidden={hiddenSet.has(id)}
                canUp={index > 0}
                canDown={index < layout.order.length - 1}
                onMove={move}
                onHide={setHidden}
              />
            </Fragment>
          )
        })}
      </ul>
      <button
        type="button"
        data-testid="settings-drawer-reset"
        onClick={() => reset()}
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium text-neutral-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
      >
        <RotateCcw className="h-3 w-3" aria-hidden="true" />
        Reset to default
      </button>
    </>
  )
  if (variant === 'inline') {
    return <div data-testid="card-tab-layout-inline">{editor}</div>
  }

  return (
    <div ref={rootRef} className="relative flex justify-end">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2 py-1 text-[11px] font-medium text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
      >
        <SlidersHorizontal className="h-3 w-3" aria-hidden="true" />
        Card layout
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Card layout"
          className="absolute right-0 top-full z-30 mt-1 w-72 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
        >
          {editor}
        </div>
      )}
    </div>
  )
}

function LayoutRow({
  id,
  hidden,
  canUp,
  canDown,
  onMove,
  onHide,
}: {
  id: CardTabBlockId
  hidden: boolean
  canUp: boolean
  canDown: boolean
  onMove: UseCardTabLayoutResult['move']
  onHide: UseCardTabLayoutResult['setHidden']
}) {
  return (
    <li
      data-testid={`settings-drawer-block-${id}`}
      className={[
        'flex items-center gap-1 rounded-md px-1 py-0.5',
        hidden ? 'opacity-60' : '',
      ].join(' ')}
    >
      <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-neutral-800 dark:text-neutral-100">
        {CARD_TAB_BLOCK_LABELS[id]}
      </span>
      <button
        type="button"
        aria-label={`Move ${CARD_TAB_BLOCK_LABELS[id]} up`}
        disabled={!canUp}
        onClick={() => onMove(id, 'up')}
        className="rounded p-1 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-neutral-800"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label={`Move ${CARD_TAB_BLOCK_LABELS[id]} down`}
        disabled={!canDown}
        onClick={() => onMove(id, 'down')}
        className="rounded p-1 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30 dark:hover:bg-neutral-800"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label={hidden ? `Show ${CARD_TAB_BLOCK_LABELS[id]}` : `Hide ${CARD_TAB_BLOCK_LABELS[id]}`}
        onClick={() => onHide(id, !hidden)}
        className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
      >
        {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </li>
  )
}
